import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { usePage } from '@inertiajs/react';

import { PlanLimitModal } from '@/components/shared/plan-limit-modal';
import { SidebarProvider } from '@/components/ui/sidebar';
import { useIsPageEditor } from '@/hooks/use-is-page-editor';
import { useIsVersionHistory } from '@/hooks/use-is-version-history';
import { AppSidebar } from '@/layouts/app/sidebar';
import { cn } from '@/lib/utils';
import { useAccountLimitsStore } from '@/stores/account-limits-store';
import type { SharedData } from '@/types/shared';

interface AppShellProps {
  children: ReactNode;
}

function useSeedAiCreditStatus() {
  const { aiCreditStatus } = usePage<SharedData>().props;
  const setAiCreditStatus = useAccountLimitsStore((state) => state.setAiCreditStatus);

  useEffect(() => {
    if (aiCreditStatus) setAiCreditStatus(aiCreditStatus);
  }, [aiCreditStatus, setAiCreditStatus]);
}

function AppShell({ children }: AppShellProps) {
  const isPageEditor = useIsPageEditor();
  const isViewingPageVersions = useIsVersionHistory();
  useSeedAiCreditStatus();

  return (
    <SidebarProvider width={isPageEditor || isViewingPageVersions ? 'wide' : 'default'}>
      {/*
        Keep mounted across version history. invisible+absolute (not
        display:none) preserves scroll and virtualizer measurements.
      */}
      <div
        className={cn(
          isViewingPageVersions && 'invisible absolute top-0 left-0 h-svh w-(--sidebar-width)',
        )}
        inert={isViewingPageVersions || undefined}
      >
        <AppSidebar />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">{children}</div>
      <PlanLimitModal />
    </SidebarProvider>
  );
}

export default AppShell;
